import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  onSnapshot,
  query,
  where,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useToast } from '@chakra-ui/react';
import { db } from '../../../../utils/init-firebase';

// Define the User type based on your application's user structure
export interface AdminUser {
  id: string; // This is the doc ID (uid)
  uid?: string; // Alternative ID field
  name: string;
  Name?: string; // Alternative name field
  email: string;
  Email?: string; // Alternative email field
  role?: string; // The "CRM Admin" or other role (maps to CRMRole in Firestore)
  CRMRole?: string; // The CRM role field from Firestore
  CRMUser?: boolean; // Whether user is a CRM user
  SuperAdmin?: boolean; // The *real* admin check
  isEOSUser?: boolean; // The new field
  canPresentMeetings?: boolean; // Whether user can present meetings
  Branch?: string; // User's branch location
  LicensedStates?: string[]; // Array of licensed states
  Roles?: string[]; // Array of role strings
  MarketingAdmin?: boolean; // Marketing admin flag
  Level1?: boolean; // Level1 flag
  NMLS?: string; // NMLS number
  SwagBudget?: string; // Swag budget
  photo?: string; // Photo URL
  phoneNo?: string[]; // Array of phone numbers
  website?: string; // Website
  dateJoined?: Date | string; // Date joined
  authProvider?: string; // Auth provider (e.g., 'google')
  isAdmin?: boolean; // Admin flag
  fcmTokens?: string[]; // FCM tokens for push notifications
  // Additional fields that may exist but aren't commonly used in admin UI
  [key: string]: any; // Allow for additional fields from Firestore
}

// Get reference to our new cloud function with the correct region (us-east1)
const functions = getFunctions(db.app, 'us-east1');
const updateUserPermissionCallable = httpsCallable(functions, 'updateUserPermission');

export const useUserManagement = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  // 1. Fetch all users (excluding SuperAdmins)
  useEffect(() => {
    // This query correctly fetches all non-SuperAdmins
    const q = query(
      collection(db, 'users'),
      where('SuperAdmin', '!=', true)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const usersData: AdminUser[] = [];
      snapshot.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
        const data = doc.data();
        
        usersData.push({
          id: doc.id,
          uid: data.uid || doc.id,
          name: data.name || data.Name || (data.firstName && data.lastName ? `${data.firstName} ${data.lastName}` : 'No Name'),
          Name: data.Name || data.name,
          email: data.email || data.Email || 'No Email',
          Email: data.Email || data.email,
          role: data.role || data.CRMRole || 'user', // This is the CRM role, for display
          CRMRole: data.CRMRole || data.role,
          CRMUser: data.CRMUser || false,
          SuperAdmin: data.SuperAdmin || false,
          isEOSUser: data.isEOSUser || false, // Default to false if field doesn't exist
          canPresentMeetings: data.canPresentMeetings || false, // Default to false if field doesn't exist
          Branch: data.Branch,
          LicensedStates: data.LicensedStates || data['Licensed States'] || [],
          Roles: data.Roles || [],
          MarketingAdmin: data.MarketingAdmin || false,
          Level1: data.Level1 || false,
          NMLS: data.NMLS,
          SwagBudget: data.SwagBudget || data['Swag Budget'],
          photo: data.photo,
          phoneNo: data.phoneNo || [],
          website: data.website,
          dateJoined: data.dateJoined,
          authProvider: data.authProvider,
          isAdmin: data.isAdmin || false,
          fcmTokens: data.fcmTokens || data['FCM Tokens'] || [],
        } as AdminUser);
      });
      // Sort by name client-side
      setUsers(usersData.sort((a, b) => a.name.localeCompare(b.name)));
      setLoading(false);
    }, (err) => {
      console.error("Error fetching users: ", err);
      toast({ 
        title: 'Error fetching users', 
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [toast]);

  // 2. Unified permission update function
  const updateUserPermission = useCallback(async (
    targetUserId: string, 
    permission: 'isEOSUser' | 'canPresentMeetings', 
    status: boolean
  ) => {
    try {
      await updateUserPermissionCallable({ targetUserId, permission, status });
      const permissionLabels = {
        isEOSUser: 'EOS Access',
        canPresentMeetings: 'Can Present Meetings'
      };
      toast({
        title: 'Permission Updated',
        description: `${permissionLabels[permission]} ${status ? 'granted' : 'revoked'}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Error updating user permission:", error);
      toast({
        title: 'Action Failed',
        description: error.message || 'Failed to update user permission',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      throw error; // Re-throw so the UI can handle it
    }
  }, [toast]);

  // Legacy functions kept for backward compatibility (can be removed later)
  const setUserStatus = useCallback(async (targetUserId: string, status: boolean) => {
    return updateUserPermission(targetUserId, 'isEOSUser', status);
  }, [updateUserPermission]);

  const toggleCanPresentMeetings = useCallback(async (targetUserId: string, status: boolean) => {
    return updateUserPermission(targetUserId, 'canPresentMeetings', status);
  }, [updateUserPermission]);

  return { users, loading, updateUserPermission, setUserStatus, toggleCanPresentMeetings };
};

